import React from 'react';
import { Smartphone, Brain, Zap, Shield, Globe, Layers, Cpu, BarChart, Cloud, Server, Database, Code } from 'lucide-react';

// Simple Icons CDN: https://cdn.simpleicons.org/{slug}/{hexcolor}
export const servicePages = {
  'ai-automation': {
    title: 'AI & Automation',
    tagline: 'Intelligent systems that work while you sleep',
    description: 'We build custom AI solutions, from LLM-powered assistants to automation pipelines, integrated directly into your existing business workflows and tools.',
    heroImage: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=800&fit=crop',
    color: '#06b6d4',
    stats: [
      { value: '30+', label: 'AI Solutions Built' },
      { value: '10×', label: 'Faster Workflows' },
      { value: '90%', label: 'Task Automation' },
      { value: '24/7', label: 'System Uptime' },
    ],
    features: [
      {
        icon: <Brain className="w-6 h-6" />,
        title: 'Custom LLM Applications',
        description: 'RAG pipelines, fine-tuned models, and production AI apps on OpenAI, Anthropic, or open-source, whatever fits your use case.',
        image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=900&h=600&fit=crop',
      },
      {
        icon: <Cpu className="w-6 h-6" />,
        title: 'Process Automation',
        description: 'End-to-end workflow automation that eliminates repetitive tasks and frees your team for higher-value work.',
        image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=900&h=600&fit=crop',
      },
      {
        icon: <BarChart className="w-6 h-6" />,
        title: 'Predictive Analytics',
        description: 'Intelligent data pipelines and predictive models that turn raw business data into actionable decisions.',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&h=600&fit=crop',
      },
    ],
    process: [
      { step: '01', title: 'Use Case Analysis', description: 'Identify the highest-ROI automation opportunities in your existing workflows.' },
      { step: '02', title: 'Data Assessment', description: 'Evaluate data quality and structure needed to make your AI solution work in production.' },
      { step: '03', title: 'Model Development', description: 'Build and iterate on models or LLM pipelines with measurable benchmarks.' },
      { step: '04', title: 'Integration', description: 'Connect to existing tools via APIs, webhooks, or direct database integration.' },
      { step: '05', title: 'Monitoring', description: 'Track performance, handle model drift, and improve accuracy continuously.' },
    ],
    tech: [
      { name: 'Python', slug: 'python', color: '3776AB' },
      { name: 'OpenAI', slug: null, color: 'FFFFFF' },
      { name: 'Hugging Face', slug: 'huggingface', color: 'FFD21E' },
      { name: 'FastAPI', slug: 'fastapi', color: '009688' },
      { name: 'TensorFlow', slug: 'tensorflow', color: 'FF6F00' },
      { name: 'PyTorch', slug: 'pytorch', color: 'EE4C2C' },
      { name: 'Docker', slug: 'docker', color: '2496ED' },
      { name: 'n8n', slug: 'n8n', color: 'EA4B71' },
    ],
  },
  'custom-software': {
    title: 'Custom Software',
    tagline: 'Software built around your business, not the other way around',
    description: 'We design and build bespoke software from the ground up, internal tools, customer platforms, and end-to-end systems engineered around your exact workflows, data, and goals.',
    heroImage: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=800&fit=crop',
    color: '#3b82f6',
    stats: [
      { value: '100%', label: 'Tailored to You' },
      { value: '30+', label: 'Systems Shipped' },
      { value: '4 wk', label: 'To First Release' },
      { value: '24/7', label: 'Reliability Focus' },
    ],
    features: [
      {
        icon: <Code className="w-6 h-6" />,
        title: 'Built for Your Workflow',
        description: 'No forcing your business into off-the-shelf tools. We model your exact processes and build around them, so the software fits the team, not the reverse.',
        image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=900&h=600&fit=crop',
      },
      {
        icon: <Layers className="w-6 h-6" />,
        title: 'Integrated & Connected',
        description: 'Your custom software talks to the tools you already run, CRMs, payment providers, internal APIs, and databases, so data flows without manual re-entry.',
        image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=900&h=600&fit=crop',
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Owned & Maintainable',
        description: 'Clean, documented code that you own outright, architected to scale and hand off, with no vendor lock-in or hidden licensing.',
        image: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=900&h=600&fit=crop',
      },
    ],
    process: [
      { step: '01', title: 'Discovery', description: 'We map your workflows, pain points, and goals into a clear technical specification.' },
      { step: '02', title: 'Architecture', description: 'System design, data models, and integrations planned before any code is written.' },
      { step: '03', title: 'Development', description: 'Two-week agile sprints with working software every cycle, no surprises at delivery.' },
      { step: '04', title: 'QA & Hardening', description: 'Automated testing, security review, and performance profiling across real scenarios.' },
      { step: '05', title: 'Launch & Support', description: 'Staged rollout, team training, and ongoing maintenance as your needs evolve.' },
    ],
    tech: [
      { name: 'TypeScript', slug: 'typescript', color: '3178C6' },
      { name: 'Node.js', slug: 'nodedotjs', color: '5FA04E' },
      { name: 'Python', slug: 'python', color: '3776AB' },
      { name: 'React', slug: 'react', color: '61DAFB' },
      { name: 'PostgreSQL', slug: 'postgresql', color: '4169E1' },
      { name: 'Docker', slug: 'docker', color: '2496ED' },
      { name: 'Redis', slug: 'redis', color: 'DC382D' },
      { name: 'AWS', slug: null, color: 'FF9900' },
    ],
  },
  'web-development': {
    title: 'Web Development',
    tagline: 'Web applications built for scale and speed',
    description: 'From marketing sites to full-stack SaaS platforms, we build web applications that are fast, accessible, and designed to handle growth from day one.',
    heroImage: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=1200&h=800&fit=crop',
    color: '#8b5cf6',
    stats: [
      { value: '80+', label: 'Sites Shipped' },
      { value: '<2s', label: 'Avg Load Time' },
      { value: '99%', label: 'Uptime SLA' },
      { value: '100%', label: 'Mobile Ready' },
    ],
    features: [
      {
        icon: <Globe className="w-6 h-6" />,
        title: 'Modern Stack',
        description: 'React and Next.js on the frontend, Node.js or FastAPI on the backend, chosen for your use case, not our habit.',
        image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=900&h=600&fit=crop',
      },
      {
        icon: <Zap className="w-6 h-6" />,
        title: 'Built for Performance',
        description: 'SSR, edge caching, and Core Web Vitals optimisation baked into the process, not bolted on at the end.',
        image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&h=600&fit=crop',
      },
      {
        icon: <Layers className="w-6 h-6" />,
        title: 'Scalable Architecture',
        description: 'API-first, cloud-native design that handles 1,000 users or 1,000,000 without a rearchitect.',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&h=600&fit=crop',
      },
    ],
    process: [
      { step: '01', title: 'Architecture', description: 'Stack selection and infrastructure decisions made upfront so nothing has to be undone later.' },
      { step: '02', title: 'Frontend', description: 'Component-driven UI with design system, accessibility, and responsive layouts.' },
      { step: '03', title: 'Backend & APIs', description: 'RESTful or GraphQL APIs, database design, auth, and third-party integrations.' },
      { step: '04', title: 'Testing', description: 'Unit, integration, and cross-browser compatibility checks before anything ships.' },
      { step: '05', title: 'Deploy', description: 'CI/CD pipelines, cloud deployment, and real-time performance monitoring.' },
    ],
    tech: [
      { name: 'React', slug: 'react', color: '61DAFB' },
      { name: 'Next.js', slug: 'nextdotjs', color: 'FFFFFF' },
      { name: 'TypeScript', slug: 'typescript', color: '3178C6' },
      { name: 'Node.js', slug: 'nodedotjs', color: '339933' },
      { name: 'PostgreSQL', slug: 'postgresql', color: '4169E1' },
      { name: 'Prisma', slug: 'prisma', color: '5A67D8' },
      { name: 'AWS', slug: null, color: 'FF9900' },
      { name: 'Vercel', slug: 'vercel', color: 'FFFFFF' },
    ],
  },
  'mobile-app-development': {
    title: 'Mobile App Development',
    tagline: 'Native & cross-platform apps that users love',
    description: 'We build high-performance iOS and Android applications using React Native and Flutter, combining cross-platform efficiency with the feel of a truly native app.',
    heroImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&h=800&fit=crop',
    color: '#3b82f6',
    stats: [
      { value: '50+', label: 'Apps Launched' },
      { value: '4.8★', label: 'Avg Store Rating' },
      { value: '3 wk', label: 'MVP Timeline' },
      { value: '2', label: 'Platforms Covered' },
    ],
    features: [
      {
        icon: <Smartphone className="w-6 h-6" />,
        title: 'Cross-Platform Excellence',
        description: 'One codebase, two platforms. React Native and Flutter deliver native-level performance without doubling your cost or timeline.',
        image: 'https://images.unsplash.com/photo-1607252650355-f7fd0460ccdb?w=900&h=600&fit=crop',
      },
      {
        icon: <Zap className="w-6 h-6" />,
        title: 'Performance First',
        description: 'Optimised rendering, smart caching, and native module integration ensure your app feels instant on every device.',
        image: 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?w=900&h=600&fit=crop',
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Secure & Scalable',
        description: 'End-to-end encryption, secure auth flows, and backend architecture built to grow with your user base.',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=900&h=600&fit=crop',
      },
    ],
    process: [
      { step: '01', title: 'Discovery', description: 'Goals, audience, and technical constraints mapped into a clear product brief.' },
      { step: '02', title: 'UI/UX Design', description: 'Wireframes and prototypes approved before a line of production code is written.' },
      { step: '03', title: 'Development', description: 'Two-week agile sprints with working demos every cycle, no surprises at delivery.' },
      { step: '04', title: 'QA & Testing', description: 'Device matrix testing, performance profiling, and edge-case coverage.' },
      { step: '05', title: 'Launch', description: 'Store submission, staged rollout, and ongoing maintenance post-launch.' },
    ],
    tech: [
      { name: 'React Native', slug: 'react', color: '61DAFB' },
      { name: 'Flutter', slug: 'flutter', color: '54C5F8' },
      { name: 'Swift', slug: 'swift', color: 'F05138' },
      { name: 'Kotlin', slug: 'kotlin', color: '7F52FF' },
      { name: 'Firebase', slug: 'firebase', color: 'FFCA28' },
      { name: 'Supabase', slug: 'supabase', color: '3ECF8E' },
      { name: 'Expo', slug: 'expo', color: 'FFFFFF' },
      { name: 'Redux', slug: 'redux', color: '764ABC' },
    ],
  },
  'cloud-backend': {
    title: 'Cloud & Backend Systems',
    tagline: 'Infrastructure that scales without limits',
    description: 'We architect and build cloud-native backend systems, APIs, databases, microservices, and infrastructure, engineered to handle growth from 100 users to 10 million.',
    heroImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=800&fit=crop',
    color: '#06b6d4',
    stats: [
      { value: '40+', label: 'Backends Built' },
      { value: '99.9%', label: 'Uptime Achieved' },
      { value: '10×', label: 'Scalability Factor' },
      { value: '60%', label: 'Avg Cost Reduction' },
    ],
    features: [
      { icon: <Cloud className="w-6 h-6" />, title: 'Cloud-Native Architecture', description: 'AWS, GCP, or Azure, we design infrastructure that auto-scales, self-heals, and never lets your users down.', image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=900&h=600&fit=crop' },
      { icon: <Server className="w-6 h-6" />, title: 'Microservices & APIs', description: 'RESTful and GraphQL APIs built on scalable microservice architectures, with full documentation and versioning.', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&h=600&fit=crop' },
      { icon: <Database className="w-6 h-6" />, title: 'Database Engineering', description: 'Schema design, query optimisation, caching strategies, and managed database solutions for every data model.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&h=600&fit=crop' },
    ],
    process: [
      { step: '01', title: 'Architecture Design', description: 'Identify requirements, traffic patterns, and design a cloud architecture that fits your scale and budget.' },
      { step: '02', title: 'Infrastructure Setup', description: 'Provision cloud resources with IaC (Terraform/CDK) for reproducibility and zero config drift.' },
      { step: '03', title: 'API Development', description: 'Build and document backend services with full test coverage and performance benchmarks.' },
      { step: '04', title: 'CI/CD Pipelines', description: 'Automated build, test, and deployment pipelines so every push is safe and fast.' },
      { step: '05', title: 'Monitoring & Ops', description: 'Logging, alerting, and incident response runbooks so your team sleeps at night.' },
    ],
    tech: [
      { name: 'AWS', slug: null, color: 'FF9900' },
      { name: 'Docker', slug: 'docker', color: '2496ED' },
      { name: 'Kubernetes', slug: 'kubernetes', color: '326CE5' },
      { name: 'Terraform', slug: 'terraform', color: '7B42BC' },
      { name: 'Node.js', slug: 'nodedotjs', color: '339933' },
      { name: 'PostgreSQL', slug: 'postgresql', color: '4169E1' },
      { name: 'Redis', slug: 'redis', color: 'DC382D' },
      { name: 'nginx', slug: 'nginx', color: '009639' },
    ],
  },
};
